import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';
import { StandardEvents, ProductSelectEvent, CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {string[]} [options] - Variant option values
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} [productImage] - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {ProductVariant[] | null} */
  #variants = null;

  /** @type {Record<string, string> | null} */
  #variantImages = null;

  /** @type {WeakSet<HTMLSelectElement>} */
  #boundStickySelects = new WeakSet();

  connectedCallback() {
    super.connectedCallback();

    this.#loadVariantsJson();
    this.#loadVariantImageMap();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(StandardEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#bindStickyOptionSelects();

    this.#getInitialQuantity();

    this.#targetAddToCartButton = this.#getMainAddToCartButton();

    const currentVariantId =
      this.dataset.currentVariantId ||
      this.#getCurrentVariantIdFromForm();

    if (currentVariantId) {
      this.#syncStickySelectsToVariantId(currentVariantId);
      this.#updateStickyImageFromMap(currentVariantId);
    }

    this.#syncStickyDisabledState();

    customElements.whenDefined('shopify-chat').then(() => {
      if (signal.aborted) return;

      if (this.#isStuck && this.#isChatActive()) {
        this.#hideStickyBar();
      }
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#buyButtonsIntersectionObserver?.disconnect();

    this.#abortController.abort();

    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }
  }

  /**
   * Watches the original buy buttons.
   * Once they have been scrolled past, the sticky bar appears.
   * It stays visible for the rest of the page.
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    this.#targetAddToCartButton =
      productForm.querySelector('[ref="addToCartButton"]');

    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      if (!entry.isIntersecting && !this.#isStuck) {
        const rect = entry.target.getBoundingClientRect();

        if (rect.bottom < 0 || rect.top < 0) {
          if (this.#isChatActive()) return;

          this.#showStickyBar();
        }
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hideStickyBar();
      }
    });

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
  }

  /**
   * Handles a click on the sticky add-to-cart button.
   */
  handleAddToCartClick = async () => {
    const mainButton =
      this.#targetAddToCartButton ||
      this.#getMainAddToCartButton();

    if (!mainButton) return;

    this.#targetAddToCartButton = mainButton;

    mainButton.dataset.puppet = 'true';
    mainButton.click();

    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (this.refs.addToCartButton.dataset.added !== 'true') {
      this.refs.addToCartButton.dataset.added = 'true';
    }

    if (
      !cartIcon ||
      !this.refs.addToCartButton ||
      !this.refs.productImage
    ) {
      return;
    }

    if (this.#resetTimeout) {
      clearTimeout(this.#resetTimeout);
    }

    const flyToCartElement =
      /** @type {FlyToCart} */
      (document.createElement('fly-to-cart'));

    flyToCartElement.classList.add('fly-to-cart--sticky');

    flyToCartElement.style.setProperty(
      'background-image',
      `url(${this.refs.productImage.src})`
    );

    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([
      this.refs.addToCartButton,
      flyToCartElement,
    ]);

    this.#resetTimeout = setTimeout(() => {
      this.refs.addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles Shopify's product selection event.
   *
   * @param {ProductSelectEvent} event
   */
  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const {
          html,
          productId,
          resource: variant,
        } = detail;

        if (
          productId &&
          String(productId) !== String(this.dataset.productId)
        ) {
          return;
        }

        const newStickyAddToCart =
          /** @type {HTMLElement | null} */
          (html.querySelector('sticky-add-to-cart'));

        if (!newStickyAddToCart) return;

        const newStickyBar =
          newStickyAddToCart.querySelector('[ref="stickyBar"]');

        if (!newStickyBar) return;

        const currentStuck =
          this.refs.stickyBar.getAttribute('data-stuck') || 'false';

        const variantAvailable =
          newStickyAddToCart.dataset.variantAvailable;

        morph(
          this.refs.stickyBar,
          newStickyBar,
          {
            childrenOnly: true,
          }
        );

        this.refs.stickyBar.setAttribute(
          'data-stuck',
          currentStuck
        );

        if (variantAvailable !== undefined) {
          this.dataset.variantAvailable = variantAvailable;
        }

        if (variant?.id) {
          const variantId = String(variant.id);

          this.dataset.currentVariantId = variantId;

          this.#updateStickyImageFromMap(variantId);
          this.#syncStickySelectsToVariantId(variantId);
        }

        this.#targetAddToCartButton =
          this.#getMainAddToCartButton();

        this.#bindStickyOptionSelects();

        this.#syncStickyDisabledState();
        this.#updateButtonText();

        if (variant == null) {
          this.#handleVariantUnavailable();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[sticky-add-to-cart] Product select promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Binds the sticky variant selectors.
   */
  #bindStickyOptionSelects() {
    const selects = this.#getStickyOptionSelects();

    if (!selects.length) return;

    const { signal } = this.#abortController;

    selects.forEach((select) => {
      if (this.#boundStickySelects.has(select)) return;

      select.addEventListener(
        'change',
        () => {
          const optionIndex =
            Number(select.dataset.optionIndex);

          this.#applyVariantFromStickySelects(optionIndex);
        },
        { signal }
      );

      this.#boundStickySelects.add(select);
    });
  }

  /**
   * Returns all option selectors inside the sticky bar.
   *
   * @returns {HTMLSelectElement[]}
   */
  #getStickyOptionSelects() {
    const stickyBar = this.refs?.stickyBar;

    if (!stickyBar) return [];

    return Array.from(
      stickyBar.querySelectorAll(
        'select[data-option-index]'
      )
    );
  }

  /**
   * Applies a sticky option selection to the main Horizon variant picker.
   *
   * @param {number} changedOptionIndex
   */
  #applyVariantFromStickySelects(changedOptionIndex) {
    const selects = this.#getStickyOptionSelects();

    if (!selects.length) return;

    const chosenOptions = [];

    selects.forEach((select) => {
      const index = Number(select.dataset.optionIndex);

      chosenOptions[index] = select.value;
    });

    const variant =
      this.#findVariantByOptions(chosenOptions);

    if (!variant) return;

    const variantPicker = document.querySelector(
      `variant-picker[data-product-id="${this.dataset.productId}"]`
    );

    if (variantPicker) {
      const fieldsets =
        Array.from(variantPicker.querySelectorAll('fieldset'));

      const fieldset =
        fieldsets[changedOptionIndex];

      if (fieldset) {
        const selectedValue =
          chosenOptions[changedOptionIndex];

        const optionInputs =
          Array.from(
            fieldset.querySelectorAll(
              'input[type="radio"], input[type="checkbox"]'
            )
          );

        const matchingInput =
          optionInputs.find(
            (input) => input.value === selectedValue
          );

        if (matchingInput) {
          if (!matchingInput.checked) {
            matchingInput.click();
          }

          return;
        }
      }

      const pickerSelects =
        Array.from(
          variantPicker.querySelectorAll('select')
        );

      const pickerSelect =
        pickerSelects[changedOptionIndex];

      if (pickerSelect) {
        const selectedValue =
          chosenOptions[changedOptionIndex];

        if (pickerSelect.value !== selectedValue) {
          pickerSelect.value = selectedValue;

          pickerSelect.dispatchEvent(
            new Event('change', {
              bubbles: true,
            })
          );
        }

        return;
      }
    }

    const nativeForm =
      this.#getProductFormNative();

    if (!nativeForm) return;

    const variantIdInput =
      nativeForm.querySelector('input[name="id"]');

    if (!variantIdInput) return;

    variantIdInput.value = String(variant.id);

    variantIdInput.dispatchEvent(
      new Event('input', {
        bubbles: true,
      })
    );

    variantIdInput.dispatchEvent(
      new Event('change', {
        bubbles: true,
      })
    );

    this.dataset.currentVariantId =
      String(variant.id);

    this.dataset.variantAvailable =
      variant.available ? 'true' : 'false';

    this.#updateStickyImageFromMap(
      String(variant.id)
    );

    this.#syncStickySelectsToVariantId(
      String(variant.id)
    );

    this.refs.addToCartButton.disabled =
      !variant.available;
  }

  /**
   * Finds a product variant that exactly matches the chosen options.
   *
   * @param {string[]} chosenOptions
   * @returns {ProductVariant | null}
   */
  #findVariantByOptions(chosenOptions) {
    if (
      !this.#variants ||
      !Array.isArray(this.#variants)
    ) {
      return null;
    }

    for (const variant of this.#variants) {
      if (
        !variant ||
        !Array.isArray(variant.options)
      ) {
        continue;
      }

      let matches = true;

      for (
        let index = 0;
        index < chosenOptions.length;
        index++
      ) {
        const chosenValue =
          chosenOptions[index];

        if (chosenValue == null) continue;

        if (
          variant.options[index] !== chosenValue
        ) {
          matches = false;
          break;
        }
      }

      if (matches) {
        return variant;
      }
    }

    return null;
  }

  /**
   * Finds a variant by ID.
   *
   * @param {string|number} id
   * @returns {ProductVariant | null}
   */
  #findVariantById(id) {
    if (
      !this.#variants ||
      !Array.isArray(this.#variants)
    ) {
      return null;
    }

    const stringId = String(id);

    for (const variant of this.#variants) {
      if (!variant) continue;

      if (
        String(variant.id) === stringId
      ) {
        return variant;
      }
    }

    return null;
  }

  /**
   * Synchronises the sticky dropdowns with the active variant.
   *
   * @param {string|number} variantId
   */
  #syncStickySelectsToVariantId(variantId) {
    if (!variantId) return;

    const variant =
      this.#findVariantById(variantId);

    if (
      !variant ||
      !Array.isArray(variant.options)
    ) {
      return;
    }

    const selects =
      this.#getStickyOptionSelects();

    if (!selects.length) return;

    selects.forEach((select) => {
      const optionIndex =
        Number(select.dataset.optionIndex);

      const value =
        variant.options[optionIndex];

      if (
        typeof value === 'string' &&
        select.value !== value
      ) {
        select.value = value;
      }
    });
  }

  /**
   * Updates the product image inside the sticky bar.
   *
   * @param {string|number} variantId
   */
  #updateStickyImageFromMap(variantId) {
    if (!this.refs.productImage) return;
    if (!this.#variantImages) return;
    if (!variantId) return;

    const url =
      this.#variantImages[String(variantId)];

    if (!url) return;

    if (this.refs.productImage.src !== url) {
      this.refs.productImage.src = url;
    }
  }

  /**
   * Handles unavailable variant combinations.
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';

    const variantTitleElement =
      this.querySelector(
        '.sticky-add-to-cart__variant'
      );

    const productId =
      this.dataset.productId;

    const variantPicker =
      document.querySelector(
        `variant-picker[data-product-id="${productId}"]`
      );

    if (
      !variantTitleElement ||
      !variantPicker
    ) {
      return;
    }

    const selectedOptions =
      Array.from(
        variantPicker.querySelectorAll(
          'input:checked'
        )
      )
        .map((option) => option.value)
        .filter((value) => value !== '')
        .join(' / ');

    if (!selectedOptions) return;

    variantTitleElement.textContent =
      selectedOptions;
  };

  /**
   * Handles successful or failed cart operations.
   *
   * @param {CartLinesUpdateEvent | CartErrorEvent} event
   */
  #handleCartAddComplete = (event) => {
    const resetPuppet = () => {
      if (this.#targetAddToCartButton) {
        this.#targetAddToCartButton.dataset.puppet =
          'false';
      }
    };

    if (
      'promise' in event &&
      event.promise instanceof Promise
    ) {
      event.promise.finally(resetPuppet);
    } else {
      resetPuppet();
    }
  };

  /**
   * Handles quantity selector updates.
   *
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity =
      event.detail.quantity;

    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar.
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = true;

    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar.
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;

    this.#isStuck = false;

    stickyBar.dataset.stuck = 'false';
  }

  /**
   * Checks whether Shopify Chat is active.
   *
   * @returns {boolean}
   */
  #isChatActive() {
    if (
      !customElements.get('shopify-chat')
    ) {
      return false;
    }

    return Boolean(
      document.querySelector('shopify-chat')
    );
  }

  /**
   * Gets the main Horizon product form component.
   *
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId =
      this.dataset.productId;

    if (!productId) return null;

    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return null;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the native Shopify product form.
   *
   * @returns {HTMLFormElement | null}
   */
  #getProductFormNative() {
    const component =
      this.#getProductForm();

    if (!component) return null;

    return (
      component.querySelector(
        'form[data-type="add-to-cart-form"]'
      ) ||
      component.querySelector(
        'form.shopify-product-form'
      )
    );
  }

  /**
   * Gets the main add-to-cart button.
   *
   * @returns {HTMLButtonElement | null}
   */
  #getMainAddToCartButton() {
    const productForm =
      this.#getProductForm();

    if (!productForm) return null;

    return (
      productForm.querySelector(
        '[ref="addToCartButton"]'
      ) ||
      productForm.querySelector(
        '.product-form-buttons .add-to-cart-button'
      ) ||
      productForm.querySelector(
        'button[type="submit"], button[name="add"]'
      )
    );
  }

  /**
   * Gets the current variant ID from the native product form.
   *
   * @returns {string}
   */
  #getCurrentVariantIdFromForm() {
    const form =
      this.#getProductFormNative();

    if (!form) return '';

    const variantIdInput =
      form.querySelector('input[name="id"]');

    return variantIdInput?.value || '';
  }

  /**
   * Gets the initial quantity.
   */
  #getInitialQuantity() {
    this.#currentQuantity =
      parseInt(
        this.dataset.initialQuantity || '1',
        10
      ) || 1;

    this.#updateButtonText();
  }

  /**
   * Updates quantity display in the sticky button.
   */
  #updateButtonText() {
    const {
      addToCartButton,
      quantityDisplay,
      quantityNumber,
    } = this.refs;

    if (
      !addToCartButton ||
      !quantityDisplay ||
      !quantityNumber
    ) {
      return;
    }

    const available =
      !addToCartButton.disabled;

    quantityNumber.textContent =
      this.#currentQuantity.toString();

    if (
      available &&
      this.#currentQuantity > 1
    ) {
      quantityDisplay.style.display =
        'inline';
    } else {
      quantityDisplay.style.display =
        'none';
    }
  }

  /**
   * Synchronises availability with the main add-to-cart button.
   */
  #syncStickyDisabledState() {
    const mainButton =
      this.#getMainAddToCartButton();

    if (!mainButton) return;

    this.#targetAddToCartButton =
      mainButton;

    const disabled =
      Boolean(mainButton.disabled);

    this.refs.addToCartButton.disabled =
      disabled;

    this.dataset.variantAvailable =
      disabled ? 'false' : 'true';
  }

  /**
   * Loads Shopify's variant JSON rendered by product-information.liquid.
   */
  #loadVariantsJson() {
    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    const element =
      sectionElement.querySelector(
        `script#sticky-variants-${sectionId}`
      ) ||
      document.getElementById(
        `sticky-variants-${sectionId}`
      );

    if (!element) return;

    try {
      const parsed =
        JSON.parse(
          element.textContent.trim()
        );

      if (Array.isArray(parsed)) {
        this.#variants = parsed;
      }
    } catch (error) {
      this.#variants = null;

      console.warn(
        '[sticky-add-to-cart] Could not parse variant JSON.',
        error
      );
    }
  }

  /**
   * Loads the variant-to-image map rendered by product-information.liquid.
   */
  #loadVariantImageMap() {
    const sectionElement =
      this.closest('.shopify-section');

    if (!sectionElement) return;

    const sectionId =
      sectionElement.id.replace(
        'shopify-section-',
        ''
      );

    const element =
      sectionElement.querySelector(
        `script#variant-image-map-${sectionId}`
      ) ||
      document.getElementById(
        `variant-image-map-${sectionId}`
      );

    if (!element) return;

    try {
      const parsed =
        JSON.parse(
          element.textContent.trim()
        );

      if (
        parsed &&
        typeof parsed === 'object'
      ) {
        this.#variantImages = parsed;
      }
    } catch (error) {
      this.#variantImages = null;

      console.warn(
        '[sticky-add-to-cart] Could not parse variant image map.',
        error
      );
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define(
    'sticky-add-to-cart',
    StickyAddToCartComponent
  );
}